// Currency conversion service for Income page
// Uses ExchangeRate-API for live exchange rates

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
let ratesCache = {};
let cacheTimestamp = null;

/**
 * Fetch exchange rates from ExchangeRate-API
 * @param {string} baseCurrency - The base currency code (USD, GBP, EUR, AUD)
 * @returns {Promise<Object>} - Object with conversion rates
 */
export const fetchExchangeRates = async (baseCurrency) => {
  try {
    const response = await fetch(
      `https://v6.exchangerate-api.com/v6/${process.env.REACT_APP_FREE_API_KEY}/latest/${baseCurrency}`
    );
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.result === "success") {
      return data.conversion_rates;
    } else {
      throw new Error("API returned error result");
    }
  } catch (error) {
    console.error("Error fetching exchange rates:", error);
    throw error;
  }
};

/**
 * Get cached rates or fetch new ones
 * @param {string} baseCurrency - The base currency code
 * @returns {Promise<Object>} - Object with conversion rates
 */
const getCachedRates = async (baseCurrency) => {
  const now = Date.now();
  
  // Check if cache is valid
  if (
    ratesCache[baseCurrency] &&
    cacheTimestamp &&
    now - cacheTimestamp < CACHE_DURATION
  ) {
    return ratesCache[baseCurrency];
  }
  
  // Fetch new rates
  try {
    const rates = await fetchExchangeRates(baseCurrency);
    ratesCache[baseCurrency] = rates;
    cacheTimestamp = now;
    return rates;
  } catch (error) {
    // If fetch fails and we have old cache, use it
    if (ratesCache[baseCurrency]) {
      console.warn("Using stale exchange rate cache due to API failure");
      return ratesCache[baseCurrency];
    }
    throw error;
  }
};

/**
 * Convert amount from one currency to another
 * @param {number} amount - The amount to convert
 * @param {string} fromCurrency - Source currency code
 * @param {string} toCurrency - Target currency code
 * @returns {Promise<number>} - Converted amount
 */
export const convertCurrency = async (amount, fromCurrency, toCurrency) => {
  if (fromCurrency === toCurrency) {
    return amount;
  }
  
  try {
    const rates = await getCachedRates(fromCurrency);
    const rate = rates[toCurrency];
    
    if (!rate) {
      throw new Error(`No rate found for ${fromCurrency} to ${toCurrency}`);
    }
    
    return amount * rate;
  } catch (error) {
    console.error("Currency conversion error:", error);
    // Fallback to 1:1 if conversion fails
    return amount;
  }
};

/**
 * Convert multiple items to a base currency
 * @param {Array} items - Array of items with {amount, currency} properties
 * @param {string} baseCurrency - Target currency code
 * @returns {Promise<Array>} - Array of items with convertedAmount and exchangeRate
 */
export const convertToBaseCurrency = async (items, baseCurrency) => {
  if (!items || items.length === 0) {
    return [];
  }
  
  try {
    // Get all unique currencies
    const uniqueCurrencies = [...new Set(items.map(item => item.currency))].filter(
      c => c && c !== baseCurrency
    );
    
    // Fetch rates for all unique currencies
    const ratesMap = {};
    for (const currency of uniqueCurrencies) {
      try {
        const rates = await getCachedRates(currency);
        ratesMap[currency] = rates[baseCurrency];
      } catch (error) {
        console.error(`Failed to get rate for ${currency}:`, error);
        ratesMap[currency] = 1; // Fallback to 1:1
      }
    }
    
    // Convert all items
    return items.map(item => {
      if (!item.currency || item.currency === baseCurrency) {
        return {
          ...item,
          convertedAmount: item.amount || 0,
          exchangeRate: 1,
          originalCurrency: item.currency || baseCurrency,
        };
      }
      
      const rate = ratesMap[item.currency] || 1;
      return {
        ...item,
        convertedAmount: (item.amount || 0) * rate,
        exchangeRate: rate,
        originalCurrency: item.currency,
      };
    });
  } catch (error) {
    console.error("Error in convertToBaseCurrency:", error);
    // Return items with 1:1 conversion as fallback
    return items.map(item => ({
      ...item,
      convertedAmount: item.amount || 0,
      exchangeRate: 1,
      originalCurrency: item.currency || baseCurrency,
    }));
  }
};

/**
 * Get currency symbol for display
 * @param {string} currencyCode - Currency code (USD, GBP, EUR, AUD, NZD, JPY, CAD, CHF)
 * @returns {string} - Currency symbol
 */
export const getCurrencySymbol = (currencyCode) => {
  const symbols = {
    USD: "$",
    GBP: "£",
    EUR: "€",
    AUD: "A$",
    NZD: "NZ$",
    JPY: "¥",
    CAD: "C$",
    CHF: "CHF",
  };
  return symbols[currencyCode] || currencyCode;
};

/**
 * Clear the exchange rate cache (useful for manual refresh)
 */
export const clearRateCache = () => {
  ratesCache = {};
  cacheTimestamp = null;
};
